import { Buffer } from "buffer";
import { useWindowSize } from "react-use";
import Confetti from "react-confetti";
import { useReactMediaRecorder } from "react-media-recorder";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import RecordScreenIcon from "../../assets/campaign/record-screen.png";
import GiftsIcon from "../../assets/campaign/gifts.png";
import WinnersIcon from "../../assets/campaign/winners.png";
import { ConfirmateAwarded } from "./ConfirmateAwarded";
import { useStyles } from "./styles";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { useMutation, useQuery } from "react-query";
import {
  findCampaignAwarded,
  randomAward,
  updateCampaignsAwarded,
} from "../../services/campaignAwarded";
import { PeopleAwarded } from "./PeopleAwarded";
import { BlobToBase64 } from "../../lib/toBase64";
import { useAws } from "../../hooks/useAws";
import { useAuth } from "../../hooks/useAuth";
import { UpdateCampaignAwardedData } from "../../types/campaignAwarded";
import { queryClient } from "../../services/queryClient";

export function CampaignAwardedId() {
  const { width, height } = useWindowSize();
  const { uploadS3 } = useAws();
  const { user } = useAuth();
  const navigate = useNavigate();
  const styles = useStyles();
  const location: { state: any } = useLocation();
  const [dialogStartAwarded, setDialogStartAwarded] = useState<boolean>(false);
  const [dialogAwarded, setDialogAwarded] = useState<boolean>(false);

  const handleStopRecordScreen = async (blobUrl: string, blob: Blob) => {
    const campaign = location.state;
    const base = await BlobToBase64(blob);
    const file = base.split(";base64,")[1];
    const uploadQrCode = {
      file: Buffer.from(file, "base64"),
      type: "video/mp4",
      bucketName: "datasalesio-imagens",
      folderName: "awarded-plus",
      subFolderName: `${user.companyId}/`,
      distinctName: true,
      extension: "mp4",
    };
    const url = await uploadS3(uploadQrCode);
    await handleSaveCaptureScreen.mutateAsync({ ...campaign, video: url });
  };

  const { startRecording, stopRecording, status } = useReactMediaRecorder({
    video: false,
    audio: true,
    screen: true,
    blobPropertyBag: { type: "video/mp4" },
    onStop: handleStopRecordScreen,
  });

  const awarded = useQuery(
    ["awarded", location.state?.id],
    () => findCampaignAwarded(location.state?.id),
    {
      enabled: Boolean(location.state?.id),
      onSuccess: (response) => {
        if (!Boolean(response.processed)) {
          setDialogStartAwarded(true);
        }
      },
    }
  );

  const handleRandomAward = useMutation(
    async () => {
      const response = await randomAward(location.state?.id);
      return response;
    },
    {
      onSuccess: () => {
        setDialogAwarded(true);
      },
    }
  );

  const handleSaveCaptureScreen = useMutation(
    async (data: UpdateCampaignAwardedData) => {
      const response = await updateCampaignsAwarded(data);
      return response;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("campaignsAwarded");
      },
    }
  );

  const handleNavigateGoToBack = useCallback(async () => {
    stopRecording();
    setDialogAwarded(false);
    navigate(-1);
  }, [navigate, stopRecording]);

  const handleDialogStartAwarded = useCallback(() => {
    startRecording();
    setDialogStartAwarded((prev) => !prev);
  }, [startRecording]);

  const handleStartAward = useCallback(async () => {
    await handleRandomAward.mutateAsync();
  }, [handleRandomAward]);

  useEffect(() => {
    return () => {
      stopRecording();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (awarded.isFetching) {
    return (
      <div className={styles.root}>
        <Typography variant="h4">{location.state["name"]}</Typography>
        <Stack spacing={1} alignItems="center" justifyContent="center">
          <CircularProgress />
        </Stack>
      </div>
    );
  }

  return (
    <>
      <div className={styles.root}>
        <Confetti run={dialogAwarded} width={width} height={height} />
        <Typography variant="h4">{location.state["name"]}</Typography>
        {Boolean(awarded.data?.processed) ? (
          <Stack spacing={1} alignItems="center">
            <img alt="prize-draw" style={{ width: "35%" }} src={WinnersIcon} />
            <Typography variant="body2">
              A premiação já foi realizada, acesse a lista de premiações para
              saber os vencedores.
            </Typography>
            <Button variant="contained" onClick={handleNavigateGoToBack}>
              Voltar
            </Button>
          </Stack>
        ) : status === "idle" || status === "acquiring_media" ? (
          <Stack spacing={1} alignItems="center">
            <img
              alt="prize-draw"
              style={{ width: "50%" }}
              src={RecordScreenIcon}
            />
            <Typography variant="body2">
              Para garantir a fidelidade da premiação, é necessário a permissão
              para capturar a tela e do microfone.
            </Typography>
            <Button variant="contained" onClick={startRecording}>
              Permitir captura
            </Button>
          </Stack>
        ) : (
          <Stack spacing={1} alignItems="center" justifyContent="center">
            <img alt="prize-draw" style={{ width: "50%" }} src={GiftsIcon} />
            {handleRandomAward.isLoading ? (
              <>
                <Typography variant="body2">
                  Preparando e escolhendo as pessoas sortudas 🏆
                </Typography>
                <CircularProgress size={26} thickness={4} />
              </>
            ) : (
              <>
                <Typography variant="body2">
                  Após o início do sorteio, não será possível editar ou refazer
                  a ação. 🔐
                </Typography>
                <Button variant="contained" onClick={handleStartAward}>
                  Iniciar premiação
                </Button>
              </>
            )}
          </Stack>
        )}
      </div>
      <ConfirmateAwarded
        open={dialogStartAwarded}
        onClose={handleDialogStartAwarded}
        onBack={handleNavigateGoToBack}
      />
      <PeopleAwarded
        awarded={handleRandomAward.data}
        open={dialogAwarded}
        onClose={handleNavigateGoToBack}
      />
    </>
  );
}
